import { useEffect, useRef, useState } from 'react';
import { api, type CardText, type CardTextPausedReason, type CardTextResult, type MorningRef } from '../../lib/api';

/**
 * MORNING2-AI Task 7 (design 3, 4.5, 6) - the AI title and reason for the cards the Morning chat shows.
 *
 * ⭐ The page never waits on the AI: it draws its plain list first, and this hook fills text in as each answer arrives. A
 *   card with no text yet, a card the answer leaves out, a failed request and a malformed answer all leave the card plain -
 *   no error, no spinner over the words (plain is the correct, free fallback).
 * ⭐ Asked in chunks of at most 6 cards, ONE AFTER ANOTHER and top of the list first (never in parallel), so each chunk fills
 *   in as it answers while the server's 3-at-a-time pool holds.
 * ⛔ Only `{ kind, id }` goes up (api.morning.cardText); the server reads everything else from HIS list.
 */

/** The key the server answers by: each ref's `kind:id`, sorted, joined with '+' (the backend's `cardKeyOf`, mirrored by hand). */
export const cardKeyOf = (refs: MorningRef[]): string => refs.map((r) => `${r.kind}:${r.id}`).sort().join('+');

/** Cards per request: the server writes 3 at a time, so a small chunk is what lets the page fill in as it goes. */
export const CARDS_PER_ASK = 6;

export interface CardTexts {
  /** By card key. A card that is not here is still plain. */
  texts: Record<string, CardTextResult>;
  /** The AI is paused today (the day's limit, or a cost that could not be recorded). Plain cards stay correct. */
  paused: boolean;
  pausedReason: Exclude<CardTextPausedReason, null> | null;
}

/** What the page holds, tagged with the read it was asked for. */
interface Held extends CardTexts { read: unknown }
const NOTHING: CardTexts = { texts: {}, paused: false, pausedReason: null };

/**
 * A ready text only when it is whole: a title, a reason and at least one source line (the server never sends fewer). Anything
 * else is plain - a half-formed text must never reach the screen, and `sources` is rendered as a list.
 */
function wellFormed(result: unknown): CardTextResult {
  const r = result as { status?: unknown; text?: { title?: unknown; reason?: unknown; sources?: unknown } } | null | undefined;
  if (r?.status !== 'ready' || !r.text) return { status: 'plain' };
  const { title, reason, sources } = r.text;
  if (typeof title !== 'string' || !title.trim() || typeof reason !== 'string' || !reason.trim() || !Array.isArray(sources)) {
    return { status: 'plain' };
  }
  const lines = sources.filter((s): s is { id: string; text: string } =>
    typeof s?.id === 'string' && typeof s?.text === 'string' && s.text.trim() !== '');
  if (lines.length === 0) return { status: 'plain' };
  const text: CardText = { title, reason, sources: lines.map(({ id, text: words }) => ({ id, text: words })) };
  return { status: 'ready', text };
}

interface Runner {
  /** The read these answers are for. A new read drops everything: the same item may have a newer message. */
  read: unknown;
  /** The card keys already asked about for this read (or queued): each is asked once. */
  asked: Set<string>;
  queue: MorningRef[][][];
  going: boolean;
  /** Bumped when a read is replaced: an answer that comes back for an older one is dropped. */
  gen: number;
  /** False once the page is gone: nothing more is asked. (Not cleared by a StrictMode dry run - it is set again at once.) */
  alive: boolean;
}

/** Answers the queue one chunk at a time. A failed chunk is skipped (its cards stay plain) and the next is still asked. */
async function drain(run: Runner, set: (change: (held: Held) => Held) => void): Promise<void> {
  if (run.going) return;
  run.going = true;
  try {
    while (run.queue.length > 0 && run.alive) {
      const chunk = run.queue.shift() as MorningRef[][];
      const { gen, read } = run;
      let answer: Awaited<ReturnType<typeof api.morning.cardText>>;
      try {
        answer = await api.morning.cardText(chunk);
      } catch {
        continue; // ⛔ no banner: plain is correct
      }
      if (gen !== run.gen) continue; // answered for a read that is gone
      // Not switched on, or (R61) the one morning AI switch is off: nothing was read, so there is nothing more to ask - and
      // nothing is paused either, so no paused line.
      if (answer.on === false || answer.ai_off === true) {
        run.queue = [];
        continue;
      }
      const given = answer.texts && typeof answer.texts === 'object' ? answer.texts : {};
      const texts = Object.fromEntries(Object.entries(given).map(([key, result]) => [key, wellFormed(result)]));
      const paused = answer.paused === true;
      const reason = answer.paused_reason === 'unrecorded' ? 'unrecorded' : 'ceiling';
      set((held) => {
        const mine: CardTexts = held.read === read ? held : NOTHING;
        // Paused stays on for the rest of this read: the day's limit does not lift between two chunks.
        return {
          read,
          texts: { ...mine.texts, ...texts },
          paused: mine.paused || paused,
          pausedReason: mine.paused ? mine.pausedReason : paused ? reason : null,
        };
      });
    }
  } finally {
    run.going = false;
  }
}

/**
 * The AI text for `cards` (one card = the items it stands for), asked once per card per `read`. `read` is the page's own
 * read of the morning: a new one means an item may have changed under the same key, so the old text is dropped (the card is
 * plain until the new answer) and the cards are asked about again - the server's per-version cache makes that cheap.
 * Cards the page shows later (Can wait, once opened) are asked then, appended after what is already queued.
 * R59: the caller decides what is a new read - the page's automatic re-read with the same cards is NOT one (see MorningBrief).
 */
export function useCardText(cards: MorningRef[][], read: unknown = 0): CardTexts {
  const [held, setHeld] = useState<Held>({ ...NOTHING, read });
  const run = useRef<Runner>({ read, asked: new Set(), queue: [], going: false, gen: 0, alive: true });
  // The card keys stand for `cards`: a new array of the same cards is not a new ask.
  const signature = cards.map(cardKeyOf).join('\n');

  // Declared first, so it is set before the ask below starts.
  useEffect(() => {
    run.current.alive = true;
    return () => { run.current.alive = false; };
  }, []);

  useEffect(() => {
    const r = run.current;
    if (r.read !== read) {
      r.read = read;
      r.asked = new Set();
      r.queue = [];
      r.gen += 1;
    }
    const fresh = cards.filter((card) => !r.asked.has(cardKeyOf(card)));
    if (fresh.length === 0) return;
    for (const card of fresh) r.asked.add(cardKeyOf(card));
    for (let i = 0; i < fresh.length; i += CARDS_PER_ASK) r.queue.push(fresh.slice(i, i + CARDS_PER_ASK));
    void drain(r, setHeld);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `signature` is `cards` by key
  }, [signature, read]);

  return held.read === read ? held : NOTHING;
}
